/**
 * SessionDetailsSection
 *
 * Modality, Place of Service, Duration, and optional date/time fields.
 *
 * Two-field date model (GDN-2017): the **Date of Service** (the billing day,
 * `date_of_service`) and the **encounter time** (the clock — start/end) are
 * independent controls.
 * - "Date of Service" writes only `date_of_service`.
 * - "Encounter date" + "Start Time" + "Duration" write only `start_time`/
 *   `end_time` (end is computed from start + duration). Editing the clock never
 *   moves the Date of Service, and vice versa.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PlaceOfServiceSelect } from '@features/billing/components/Claim/PlaceOfServiceSelect';
import { Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { getModalityPlaceOfServiceError } from '@utils/modalityPlaceOfService';

import type { Modality } from '../../types/attestations';

const DURATION_PRESETS = [10, 20, 30, 60];

interface SessionDetailsSectionProps {
  modality: Modality | null;
  onModalityChange: (modality: Modality) => void;
  placeOfService: string | null;
  onPlaceOfServiceChange: (pos: string | null) => void;
  duration: number | null;
  onDurationChange: (minutes: number) => void;
  editable?: boolean;
  // Date/time fields (note view only)
  startTime?: string | null;
  endTime?: string | null;
  encounterDate?: string | null;
  /** Canonical service date (date_of_service), date-only "YYYY-MM-DD". Defaults
   * the Date of Service control; independent of the encounter clock. */
  dateOfService?: string | null;
  onTimeChange?: (updates: {
    startTime?: string;
    endTime?: string;
    /** Date-only "YYYY-MM-DD" billing day — writes date_of_service only. */
    dateOfService?: string;
  }) => void;
}

/** Format a Date to HH:mm for display */
const formatTime = (d: Date): string =>
  d.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });

/** Format a Date to h:mm a for friendly display */
const formatTimeFriendly = (d: Date): string =>
  d
    .toLocaleTimeString('en-US', {
      hour12: true,
      hour: 'numeric',
      minute: '2-digit',
    })
    .toLowerCase();

export const SessionDetailsSection: React.FC<SessionDetailsSectionProps> = ({
  modality,
  onModalityChange,
  placeOfService,
  onPlaceOfServiceChange,
  duration,
  onDurationChange,
  editable = true,
  startTime,
  encounterDate,
  dateOfService,
  onTimeChange,
}) => {
  const showTimeFields = onTimeChange !== undefined;

  // Local state for custom duration input
  const [customDuration, setCustomDuration] = useState('');
  const isPreset = duration !== null && DURATION_PRESETS.includes(duration);
  const [isCustomMode, setIsCustomMode] = useState(!isPreset && duration !== null);

  // Local state for time editing (date + start only; end is computed)
  const [localDate, setLocalDate] = useState('');
  const [localStart, setLocalStart] = useState('');
  // Local state for the Date of Service (billing day), independent of the clock.
  const [localDos, setLocalDos] = useState('');
  const [attemptedBillingError, setAttemptedBillingError] = useState<string | null>(null);
  const currentBillingError = getModalityPlaceOfServiceError({ modality, placeOfService });
  const billingError = attemptedBillingError ?? currentBillingError;

  useEffect(() => {
    if (!currentBillingError) {
      setAttemptedBillingError(null);
    }
  }, [currentBillingError]);

  // Initialize time fields from props.
  // IMPORTANT: Both date and time must use the same timezone (local).
  // Using UTC date + local time causes a day-shift on save when the UTC
  // time crosses a day boundary in the user's timezone (e.g., UTC 02:45
  // is 22:45 previous day in EDT), creating an infinite save/refetch loop.
  const toLocalDateString = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (encounterDate) {
      const d = new Date(encounterDate);
      if (!isNaN(d.getTime())) {
        setLocalDate(toLocalDateString(d));
      }
    }
    if (startTime) {
      const d = new Date(startTime);
      if (!isNaN(d.getTime())) {
        setLocalDate(prev => prev || toLocalDateString(d));
        setLocalStart(formatTime(d));
      }
    }
  }, [startTime, encounterDate]);

  // Initialize the Date of Service from the canonical service date. It is a
  // date-only value, so anchor it to local midnight to avoid a UTC day-shift.
  useEffect(() => {
    if (dateOfService) {
      const d = new Date(`${dateOfService}T00:00:00`);
      if (!isNaN(d.getTime())) {
        setLocalDos(toLocalDateString(d));
      }
    }
  }, [dateOfService]);

  // Compute end time display from start + duration
  const endTimeDisplay = (() => {
    if (!localDate || !localStart || !duration) return null;
    const start = new Date(`${localDate}T${localStart}`);
    if (isNaN(start.getTime())) return null;
    const end = new Date(start.getTime() + duration * 60 * 1000);
    return formatTimeFriendly(end);
  })();

  // Save time change — validates dates before calling onTimeChange
  const saveTimeChange = useCallback(
    (date: string, start: string, durationMin: number | null) => {
      if (!date || !start) return;

      const startDate = new Date(`${date}T${start}`);
      if (isNaN(startDate.getTime())) return;

      const startISO = startDate.toISOString();
      let endISO: string | undefined;
      if (durationMin && durationMin > 0) {
        endISO = new Date(startDate.getTime() + durationMin * 60 * 1000).toISOString();
      }

      // Clock only — start_time/end_time. The encounter clock is independent of
      // the Date of Service (the billing day), which has its own control.
      onTimeChange?.({
        startTime: startISO,
        endTime: endISO,
      });
    },
    [onTimeChange],
  );

  // Date of Service edits are independent — they write only date_of_service and
  // never touch the encounter clock. Commit on blur.
  const handleDosBlur = useCallback(() => {
    if (!localDos) return;
    onTimeChange?.({ dateOfService: localDos });
  }, [localDos, onTimeChange]);

  // Date/time change handlers update local state only — save happens on blur
  // This prevents the native date picker on Safari/iOS from entering a
  // scroll feedback loop caused by re-renders during picker interaction.
  const handleDateChange = useCallback((value: string) => {
    setLocalDate(value);
  }, []);

  const handleStartChange = useCallback((value: string) => {
    setLocalStart(value);
  }, []);

  const handleModalitySelect = useCallback(
    (value: Modality) => {
      setAttemptedBillingError(null);
      onModalityChange(value);
    },
    [onModalityChange],
  );

  const handlePlaceOfServiceSelect = useCallback(
    (value: string) => {
      const nextPlaceOfService = value || null;
      const error = getModalityPlaceOfServiceError({
        modality,
        placeOfService: nextPlaceOfService,
      });
      if (error) {
        setAttemptedBillingError(error);
        return;
      }

      setAttemptedBillingError(null);
      onPlaceOfServiceChange(nextPlaceOfService);
    },
    [modality, onPlaceOfServiceChange],
  );

  // Save on blur — commit the current date/time when user finishes interaction
  const handleTimeFieldBlur = useCallback(() => {
    saveTimeChange(localDate, localStart, duration);
  }, [localDate, localStart, duration, saveTimeChange]);

  // When duration changes (via button click), save immediately with current values
  const prevDurationRef = useRef(duration);
  useEffect(() => {
    if (
      showTimeFields &&
      localDate &&
      localStart &&
      duration !== null &&
      duration !== prevDurationRef.current
    ) {
      prevDurationRef.current = duration;
      saveTimeChange(localDate, localStart, duration);
    }
  }, [duration, showTimeFields, localDate, localStart, saveTimeChange]);

  return (
    <Stack spacing={2}>
      {/* Modality */}
      <FormControl fullWidth size="small" disabled={!editable}>
        <InputLabel>Modality</InputLabel>
        <Select
          value={modality ?? ''}
          onChange={e => handleModalitySelect(e.target.value)}
          label="Modality"
        >
          <MenuItem value="in_person">In-person</MenuItem>
          <MenuItem value="video">Video</MenuItem>
          <MenuItem value="audio">Audio</MenuItem>
        </Select>
      </FormControl>

      {/* Place of Service */}
      <PlaceOfServiceSelect
        value={placeOfService ?? ''}
        onChange={e => handlePlaceOfServiceSelect(e.target.value)}
        label="Place of Service"
        error={!!billingError}
        helperText={billingError}
        disabled={!editable}
        sx={{
          width: '100%',
          minWidth: '100%',
          maxWidth: '100%',
          '& .MuiInputBase-input': {
            padding: '8.5px 14px',
            paddingRight: '32px',
            fontSize: '0.875rem',
            textAlign: 'left',
          },
          '& .MuiOutlinedInput-root': {
            height: 'auto',
          },
        }}
      />

      {/* Date of Service — the billing day (CMS-1500 Box 24A). Independent of
          the encounter clock below. */}
      {showTimeFields && (
        <Box>
          <TextField
            type="date"
            label="Date of Service"
            value={localDos}
            onChange={e => setLocalDos(e.target.value)}
            onBlur={handleDosBlur}
            fullWidth
            size="small"
            disabled={!editable}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}
          >
            The billing date for the claim. For a dictation, this is the visit
            date — not when it was recorded.
          </Typography>
        </Box>
      )}

      {/* Encounter time (the clock) — when the session actually happened.
          Writes start_time / end_time only; never the Date of Service. */}
      {showTimeFields && (
        <>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', mt: 0.5, display: 'block', fontWeight: 600 }}
          >
            Encounter time
          </Typography>
          <TextField
            type="date"
            label="Encounter date"
            value={localDate}
            onChange={e => handleDateChange(e.target.value)}
            onBlur={handleTimeFieldBlur}
            fullWidth
            size="small"
            disabled={!editable}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            type="time"
            label="Start Time"
            value={localStart}
            onChange={e => handleStartChange(e.target.value)}
            onBlur={handleTimeFieldBlur}
            fullWidth
            size="small"
            disabled={!editable || !localDate}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </>
      )}

      {/* Duration */}
      <Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
          Duration (minutes)
        </Typography>
        <ButtonGroup size="small" fullWidth disabled={!editable} sx={{ mb: isCustomMode ? 1 : 0 }}>
          {DURATION_PRESETS.map(preset => (
            <Button
              key={preset}
              variant={duration === preset && !isCustomMode ? 'contained' : 'outlined'}
              onClick={() => {
                onDurationChange(preset);
                setCustomDuration('');
                setIsCustomMode(false);
              }}
            >
              {preset}
            </Button>
          ))}
          <Tooltip title="Custom duration">
            <Button
              variant={isCustomMode ? 'contained' : 'outlined'}
              onClick={() => setIsCustomMode(true)}
              sx={{ minWidth: 36, px: 0 }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </Button>
          </Tooltip>
        </ButtonGroup>
        {isCustomMode && (
          <TextField
            type="number"
            size="small"
            fullWidth
            placeholder="Enter minutes"
            value={
              customDuration ||
              (duration && !DURATION_PRESETS.includes(duration) ? String(duration) : '')
            }
            onChange={e => {
              setCustomDuration(e.target.value);
              const val = parseInt(e.target.value, 10);
              if (val > 0) onDurationChange(val);
            }}
            disabled={!editable}
            slotProps={{ htmlInput: { min: 1 } }}
          />
        )}
        {/* Computed end time display */}
        {showTimeFields && endTimeDisplay && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
            Ends at {endTimeDisplay}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};
